import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  checkMemberApi,
  tempRegisterApi,
  earnPointsApi,
  redeemPointsApi,
  getLoyaltyConfigApi,
} from "../../services/loyalty.service";
import { showToast } from "../../lib/global";
import { getLocalStorage } from "../../lib/session";

// Material Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import ShareIcon from "@mui/icons-material/Share";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import PaymentIcon from "@mui/icons-material/Payment";
import InfoIcon from "@mui/icons-material/Info";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";

const COUNTRIES = [
  { code: "+60", label: "🇲🇾 +60" },
  { code: "+62", label: "🇮🇩 +62" },
  { code: "+65", label: "🇸🇬 +65" },
  { code: "+66", label: "🇹🇭 +66" },
  { code: "+63", label: "🇵🇭 +63" },
  { code: "+84", label: "🇻🇳 +84" },
  { code: "+673", label: "🇧🇳 +673" },
  { code: "+855", label: "🇰🇭 +855" },
  { code: "+95", label: "🇲🇲 +95" },
  { code: "+856", label: "🇱🇦 +856" },
  { code: "+61", label: "🇦🇺 +61" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+86", label: "🇨🇳 +86" },
  { code: "+81", label: "🇯🇵 +81" },
  { code: "+82", label: "🇰🇷 +82" },
  { code: "+91", label: "🇮🇳 +91" },
];

const OpsLoyaltyPage = () => {
  const history = useHistory();
  const staffUsername = getLocalStorage("userName") || "Ops Staff";
  const [themeMode, setThemeMode] = useState(localStorage.getItem("opsTheme") || "dark");

  const toggleTheme = () => {
    const nextTheme = themeMode === "dark" ? "light" : "dark";
    setThemeMode(nextTheme);
    localStorage.setItem("opsTheme", nextTheme);
  };

  // Flow State: null (landing), "earn", "redeem"
  const [flow, setFlow] = useState(null);

  // Flow step sub-states
  // Earn Steps: 1 (Check Member), 2 (Quick Register), 3 (Enter Purchase Amount), "success_share", "success_earn"
  const [earnStep, setEarnStep] = useState(1);
  // Redeem Steps: 1 (Check Member), 2 (Redeem Entry), "success_redeem"
  const [redeemStep, setRedeemStep] = useState(1);

  // Form input states
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+60");
  const [localPhone, setLocalPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [studentInfo, setStudentInfo] = useState(null);

  // Quick Register input states
  const [regName, setRegName] = useState("");
  const [regStudentId, setRegStudentId] = useState("");
  const [regParentPhone, setRegParentPhone] = useState("");
  const [tempTokenData, setTempTokenData] = useState(null);

  // Earn input states
  const [earnAmount, setEarnAmount] = useState("");
  const [earnPosRef, setEarnPosRef] = useState("");
  const [earnResult, setEarnResult] = useState(null);

  // Redeem input states
  const [redeemPoints, setRedeemPoints] = useState("");
  const [redeemAmountRm, setRedeemAmountRm] = useState("");
  const [redeemPosRef, setRedeemPosRef] = useState("");
  const [redeemResult, setRedeemResult] = useState(null);
  const [redeemRate, setRedeemRate] = useState(10);

  // Exit/Logout function
  const handleExitSession = () => {
    localStorage.clear();
    showToast("Logged out successfully!", "success");
    history.push("/auth");
  };

  // Reset all flow states
  const handleBackToLanding = () => {
    setFlow(null);
    setEarnStep(1);
    setRedeemStep(1);
    setPhone("");
    setLocalPhone("");
    setCountryCode("+60");
    setStudentInfo(null);
    setRegName("");
    setRegStudentId("");
    setRegParentPhone("");
    setTempTokenData(null);
    setEarnAmount("");
    setEarnPosRef("");
    setEarnResult(null);
    setRedeemPoints("");
    setRedeemAmountRm("");
    setRedeemPosRef("");
    setRedeemResult(null);
  };

  // Fetch dynamic configurations on load
  useEffect(() => {
    const fetchLoyaltyConfig = async () => {
      try {
        const response = await getLoyaltyConfigApi();
        if (response?.data?.RedeemPoint) {
          setRedeemRate(parseFloat(response.data.RedeemPoint));
        }
      } catch (err) {
        console.error("Failed to fetch loyalty configurations:", err);
      }
    };
    fetchLoyaltyConfig();
  }, []);

  // Check student member API call
  const handleCheckMember = async (e) => {
    if (e) e.preventDefault();
    
    let cleanLocal = localPhone.replace(/[^0-9]/g, "");
    if (cleanLocal.startsWith("0")) {
      cleanLocal = cleanLocal.slice(1);
    }

    if (!cleanLocal) {
      showToast("Please enter a WhatsApp phone number!", "error");
      return;
    }

    if (cleanLocal.length < 8) {
      showToast("WhatsApp local number is too short! Minimum is 8 digits.", "error");
      return;
    }

    if (cleanLocal.length > 13) {
      showToast("WhatsApp local number is too long! Maximum is 13 digits.", "error");
      return;
    }

    const fullPhone = countryCode.replace("+", "") + cleanLocal;

    setLoading(true);
    try {
      const response = await checkMemberApi(fullPhone);
      if (response?.data?.found && response?.data?.members?.length > 0) {
        const student = response.data.members[0];
        setStudentInfo(student);
        if (flow === "earn") {
          setEarnStep(3); // Go directly to Purchase Entry
        } else if (flow === "redeem") {
          if (student.status === "PENDING") {
            // Pending users cannot redeem points
            showToast("Cannot redeem points: Member account is pending verification.", "warning");
          } else {
            setRedeemStep(2); // Go to redemption entry
          }
        }
      } else {
        setStudentInfo(null);
        if (flow === "earn") {
          setEarnStep(2); // Go to quick register
          setRegParentPhone(fullPhone); // Prepopulate phone field
        } else if (flow === "redeem") {
          showToast("Member not registered. Redeem is only for verified members.", "error", 20000);
        }
      }
    } catch (err) {
      console.error(err);
      showToast("Error checking member status. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Register temporary account API call
  const handleTempRegister = async (e) => {
    e.preventDefault();
    if (!regName || !regParentPhone) {
      showToast("Name and WhatsApp number are required!", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await tempRegisterApi({
        name: regName,
        student_id: regStudentId,
        phone: regParentPhone,
      });

      if (response?.data?.success) {
        setTempTokenData(response.data);
        setEarnStep("success_share");
        // Pre-fill phone field and name for amount entry
        setPhone(regParentPhone);
        setStudentInfo({
          user_id: response.data.user_id,
          name: regName,
          student_id: regStudentId,
          phone: regParentPhone,
          status: "PENDING",
        });
        showToast("Temporary member account created!", "success");
      } else {
        showToast(response?.data?.message || "Registration failed.", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Error creating temporary account. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Open WhatsApp Link for registration link sharing
  const handleShareWhatsApp = () => {
    let url = tempTokenData?.whatsapp_url;
    if (url) {
      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        url = "https://" + url;
      }
      window.open(url, "_blank");
    } else {
      // Build fallback link if URL is missing
      const msg = `Hi ${regName}, you earned loyalty points! Complete your account to unlock your points: https://yourapp.com/complete-signup?token=${tempTokenData?.temp_token}`;
      const fallbackUrl = `https://wa.me/${regParentPhone}?text=${encodeURIComponent(msg)}`;
      window.open(fallbackUrl, "_blank");
    }
  };

  // Award points API call
  const handleEarnPoints = async (e) => {
    e.preventDefault();
    if (!earnAmount || !earnPosRef) {
      showToast("All fields are required!", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await earnPointsApi({
        user_id: studentInfo?.user_id,
        amount: parseFloat(earnAmount),
        pos_ref: earnPosRef,
      });

      if (response?.data?.success) {
        setEarnResult(response.data);
        setEarnStep("success_earn");
        showToast("Points recorded successfully!", "success");
      } else {
        showToast(response?.data?.message || "Failed to award points.", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Error recording points transaction. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Auto-calculate RM equivalent when points to redeem change
  useEffect(() => {
    if (redeemPoints) {
      const calc = (parseFloat(redeemPoints) / redeemRate).toFixed(2);
      setRedeemAmountRm(calc);
    } else {
      setRedeemAmountRm("");
    }
  }, [redeemPoints, redeemRate]);

  // Redeem points API call
  const handleRedeemPoints = async (e) => {
    e.preventDefault();
    if (!redeemPoints || !redeemAmountRm || !redeemPosRef) {
      showToast("All fields are required!", "error");
      return;
    }

    if (parseFloat(redeemPoints) > studentInfo?.points_balance) {
      showToast("Insufficient point balance!", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await redeemPointsApi({
        user_id: studentInfo?.user_id,
        points: parseInt(redeemPoints, 10),
        amount_rm: parseFloat(redeemAmountRm),
        pos_ref: redeemPosRef,
      });

      if (response?.data?.success) {
        setRedeemResult({
          ...response.data,
          points_deducted: parseInt(redeemPoints, 10),
          amount_rm: parseFloat(redeemAmountRm),
          pos_ref: redeemPosRef,
        });
        setRedeemStep("success_redeem");
        showToast("Points redeemed successfully!", "success");
      } else {
        showToast(response?.data?.message || "Redemption failed.", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Error processing points redemption. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`ops-loyalty-wrapper ${themeMode}`}>
      {/* Top Header Bar */}
      <header className="ops-header">
        <div className="logo-section">
          <img src="/icons/flexLogo.png" alt="No Joke Logo" className="header-logo" />
          <span className="logo-text">No Joke</span>
          <span className="badge-offline">Offline Ops</span>
        </div>
        <div className="user-section">
          <button className="btn-theme-toggle" onClick={toggleTheme}>
            {themeMode === "dark" ? <LightModeIcon className="icon-theme" /> : <DarkModeIcon className="icon-theme" />}
            <span className="btn-text">{themeMode === "dark" ? "Light" : "Dark"}</span>
          </button>
          <span className="user-welcome">
            Staff: <strong>{staffUsername}</strong>
          </span>
          <button className="btn-exit" onClick={handleExitSession}>
            <ExitToAppIcon className="icon-exit" />
            <span className="btn-text">Exit Session</span>
          </button>
        </div>
      </header>

      {/* Main Container */}
      <main className="ops-main-container">
        {/* Landing View: Flow Selection */}
        {flow === null && (
          <div className="landing-view fade-in">
            <h1 className="landing-title">Member Loyalty Campaign</h1>
            <p className="landing-subtitle">Offline transaction operator console for member reward processing</p>

            <div className="cards-grid">
              {/* Earn Points Card */}
              <div className="glow-card earn-card" onClick={() => setFlow("earn")}>
                <div className="card-overlay"></div>
                <div className="card-content">
                  <div className="icon-wrapper bg-green">
                    <LocalActivityIcon className="card-icon" />
                  </div>
                  <h2 className="card-title text-green">EARN POINTS</h2>
                  <p className="card-description">
                    Register members & credit loyalty points for purchases
                  </p>
                  <button className="card-action-btn bg-green-gradient">Start Earn Flow</button>
                </div>
              </div>

              {/* Redeem Points Card */}
              <div className="glow-card redeem-card" onClick={() => setFlow("redeem")}>
                <div className="card-overlay"></div>
                <div className="card-content">
                  <div className="icon-wrapper bg-orange">
                    <PaymentIcon className="card-icon" />
                  </div>
                  <h2 className="card-title text-orange">REDEEM POINTS</h2>
                  <p className="card-description">
                    Deduct points from verified member balances to apply purchase discounts
                  </p>
                  <button className="card-action-btn bg-orange-gradient">Start Redeem Flow</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Earn Points Flow */}
        {flow === "earn" && (
          <div className="flow-container fade-in">
            <div className="flow-header">
              <button className="btn-back" onClick={handleBackToLanding}>
                <ArrowBackIcon /> Back to Menu
              </button>
              <div className="flow-title-wrapper">
                <span className="flow-badge bg-green">EARN FLOW</span>
                <h2>Record Member Purchase</h2>
              </div>
            </div>

            {/* Step 1: Member Phone Check */}
            {earnStep === 1 && (
              <div className="form-card glass-panel fade-in">
                <h3>Step 1: Check Member Account</h3>
                <p className="step-instructions">Enter WhatsApp phone number to check if member is registered.</p>
                <form onSubmit={handleCheckMember}>
                  <div className="input-group">
                    <label htmlFor="check-phone">WhatsApp Number</label>
                    <div className="phone-input-container">
                      <select 
                        value={countryCode} 
                        onChange={(e) => setCountryCode(e.target.value)}
                        className="country-select"
                        disabled={loading}
                      >
                        {COUNTRIES.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                      <div className="input-with-icon flex-grow-1">
                        <SearchIcon className="input-icon" />
                        <input
                          type="tel"
                          id="check-phone"
                          value={localPhone}
                          onChange={(e) => setLocalPhone(e.target.value.replace(/[^0-9]/g, ""))}
                          placeholder="e.g. 199999999"
                          disabled={loading}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <button type="submit" className="btn-submit bg-green-gradient" disabled={loading}>
                    {loading ? "Searching..." : "Check Registered Status"}
                  </button>
                </form>
              </div>
            )}

            {/* Step 2: Quick Register */}
            {earnStep === 2 && (
              <div className="form-card glass-panel fade-in">
                <div className="warning-banner">
                  <InfoIcon className="warn-icon" />
                  <div>
                    <strong>Member not registered!</strong> Complete quick offline onboarding below.
                  </div>
                </div>
                <h3>Step 2: Quick Onboarding</h3>
                <p className="step-instructions">Collect minimal info. Member completes signup later via WhatsApp link.</p>
                <form onSubmit={handleTempRegister}>
                  <div className="input-group">
                    <label htmlFor="reg-phone">WhatsApp Number <span className="text-danger">*</span></label>
                    <input
                      type="tel"
                      id="reg-phone"
                      value={regParentPhone}
                      placeholder="e.g. 60199999999"
                      disabled={loading}
                      readOnly={true}
                      required
                    />
                  </div>
                  <div className="input-group">
                    <label htmlFor="reg-name">Member Full Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      id="reg-name"
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      placeholder="e.g. Ali Bin Ahmad"
                      disabled={loading}
                      required
                    />
                  </div>
                  <div className="input-group">
                    <label htmlFor="reg-student-id">Nickname / ID number / Reference</label>
                    <input
                      type="text"
                      id="reg-student-id"
                      value={regStudentId}
                      onChange={(e) => setRegStudentId(e.target.value)}
                      placeholder="e.g. Nickname / ID / Reference (Optional)"
                      disabled={loading}
                    />
                  </div>
                  <button type="submit" className="btn-submit bg-green-gradient" disabled={loading}>
                    {loading ? "Creating Temp Account..." : "Create Temporary Account"}
                  </button>
                </form>
              </div>
            )}

            {/* Step 2 Success: Share WhatsApp Link */}
            {earnStep === "success_share" && (
              <div className="form-card glass-panel success-panel fade-in">
                <div className="success-icon-wrapper ring-green">
                  <CheckCircleOutlineIcon className="success-icon text-green" />
                </div>
                <h3>Temp Account Created!</h3>
                <p className="success-message">
                  Onboarding complete. You must share the verification link with the member via WhatsApp to lock in points.
                </p>
                
                <div className="details-box">
                  <p><strong>Member Name:</strong> {regName}</p>
                  <p><strong>Temp Token:</strong> <code className="token-code">{tempTokenData?.temp_token}</code></p>
                </div>

                <div className="action-buttons">
                  <button className="btn-action bg-whatsapp" onClick={handleShareWhatsApp}>
                    <ShareIcon className="action-icon" /> Share via WhatsApp
                  </button>
                  <button className="btn-action btn-secondary" onClick={() => setEarnStep(3)}>
                    Continue to Enter Purchase Amount
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Enter Purchase Details */}
            {earnStep === 3 && (
              <div className="form-card glass-panel fade-in">
                <div className="student-profile-summary">
                  <div className="avatar">{studentInfo?.name?.charAt(0)?.toUpperCase()}</div>
                  <div className="profile-details">
                    <h4>{studentInfo?.name}</h4>
                    <p>ID: {studentInfo?.student_id} | Status: <span className={`status-pill ${studentInfo?.status?.toLowerCase()}`}>{studentInfo?.status || "TEMP"}</span></p>
                  </div>
                </div>

                <h3>Step 3: Enter Purchase Amount</h3>
                <p className="step-instructions">Record purchase amount to credit points (Earning rate defined by system: RM 1 = 1 point).</p>
                
                <form onSubmit={handleEarnPoints}>
                  <div className="input-group">
                    <label htmlFor="earn-amount">Purchase Amount (RM)</label>
                    <input
                      type="number"
                      step="0.01"
                      id="earn-amount"
                      value={earnAmount}
                      onChange={(e) => setEarnAmount(e.target.value)}
                      placeholder="e.g. 25.50"
                      disabled={loading}
                      required
                    />
                  </div>
                  <div className="input-group">
                    <label htmlFor="earn-pos-ref">Receipt / POS Reference Number</label>
                    <input
                      type="text"
                      id="earn-pos-ref"
                      value={earnPosRef}
                      onChange={(e) => setEarnPosRef(e.target.value)}
                      placeholder="e.g. POS-0012"
                      disabled={loading}
                      required
                    />
                  </div>
                  <button type="submit" className="btn-submit bg-green-gradient" disabled={loading}>
                    {loading ? "Recording transaction..." : "Award Loyalty Points"}
                  </button>
                </form>
              </div>
            )}

            {/* Step 3 Success: Points Awarded */}
            {earnStep === "success_earn" && (
              <div className="form-card glass-panel success-panel fade-in">
                <div className="success-icon-wrapper ring-green">
                  <CheckCircleOutlineIcon className="success-icon text-green" />
                </div>
                <h3 className="text-green">Points Queue Recorded!</h3>
                <p className="success-message">
                  {studentInfo?.status === "PENDING"
                    ? "Transaction queued as PENDING_SYNC. Points will show up in member account within 24 hours of completing verification."
                    : "Points credited to member account successfully or queued for next database sync."}
                </p>

                <div className="details-box">
                  <p><strong>Member Name:</strong> {studentInfo?.name}</p>
                  <p><strong>Points Awarded:</strong> {earnResult?.points_awarded || 0} pts</p>
                  <p><strong>Transaction Ref:</strong> {earnPosRef}</p>
                  <p><strong>Sync Status:</strong> <span className="status-pill status-queued">{earnResult?.sync_status || "QUEUED"}</span></p>
                </div>

                <button className="btn-submit bg-green-gradient" onClick={handleBackToLanding}>
                  Return to Home
                </button>
              </div>
            )}
          </div>
        )}

        {/* Redeem Points Flow */}
        {flow === "redeem" && (
          <div className="flow-container fade-in">
            <div className="flow-header">
              <button className="btn-back" onClick={handleBackToLanding}>
                <ArrowBackIcon /> Back to Menu
              </button>
              <div className="flow-title-wrapper">
                <span className="flow-badge bg-orange">REDEEM FLOW</span>
                <h2>Redeem Points for Discount</h2>
              </div>
            </div>

            {/* Step 1: Member Phone Check */}
            {redeemStep === 1 && (
              <div className="form-card glass-panel fade-in">
                <h3>Step 1: Check Point Balance</h3>
                <p className="step-instructions">Enter WhatsApp phone number. Only verified members can redeem points.</p>
                <form onSubmit={handleCheckMember}>
                  <div className="input-group">
                    <label htmlFor="redeem-phone">WhatsApp Number</label>
                    <div className="phone-input-container">
                      <select 
                        value={countryCode} 
                        onChange={(e) => setCountryCode(e.target.value)}
                        className="country-select"
                        disabled={loading}
                      >
                        {COUNTRIES.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                      <div className="input-with-icon flex-grow-1">
                        <SearchIcon className="input-icon" />
                        <input
                          type="tel"
                          id="redeem-phone"
                          value={localPhone}
                          onChange={(e) => setLocalPhone(e.target.value.replace(/[^0-9]/g, ""))}
                          placeholder="e.g. 199999999"
                          disabled={loading}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <button type="submit" className="btn-submit bg-orange-gradient" disabled={loading}>
                    {loading ? "Searching..." : "Check Point Balance"}
                  </button>
                </form>
              </div>
            )}

            {/* Step 2: Redeem Entry */}
            {redeemStep === 2 && (
              <div className="form-card glass-panel fade-in">
                <div className="student-profile-summary">
                  <div className="avatar orange-avatar">{studentInfo?.name?.charAt(0)?.toUpperCase()}</div>
                  <div className="profile-details">
                    <h4>{studentInfo?.name}</h4>
                    <p>Current Balance: <strong className="text-orange">{studentInfo?.points_balance || 0} Points</strong></p>
                  </div>
                </div>

                <h3>Step 2: Enter Redemption Details</h3>
                <p className="step-instructions">Enter points to redeem. Auto-calculates RM cash value for the register drawer discount.</p>
                
                <form onSubmit={handleRedeemPoints}>
                  <div className="input-group">
                    <label htmlFor="redeem-points">Points to Redeem</label>
                    <input
                      type="number"
                      id="redeem-points"
                      value={redeemPoints}
                      onChange={(e) => setRedeemPoints(e.target.value.replace(/[^0-9]/g, ""))}
                      placeholder={`Max ${studentInfo?.points_balance || 0}`}
                      max={studentInfo?.points_balance || 0}
                      disabled={loading}
                      required
                    />
                  </div>
                  <div className="input-group">
                    <label htmlFor="redeem-val">Equivalent Value (RM)</label>
                    <input
                      type="number"
                      step="0.01"
                      id="redeem-val"
                      value={redeemAmountRm}
                      readOnly={true}
                      placeholder="0.00"
                      className="calculated-value"
                    />
                    <small className="help-text">Conversion Rate: {redeemRate} points = RM 1.00</small>
                  </div>
                  <div className="input-group">
                    <label htmlFor="redeem-pos-ref">POS Reference Number (for reconciliation)</label>
                    <input
                      type="text"
                      id="redeem-pos-ref"
                      value={redeemPosRef}
                      onChange={(e) => setRedeemPosRef(e.target.value)}
                      placeholder="e.g. POS-0015"
                      disabled={loading}
                      required
                    />
                  </div>
                  <button type="submit" className="btn-submit bg-orange-gradient" disabled={loading}>
                    {loading ? "Processing points..." : "Deduct Points & Apply Discount"}
                  </button>
                </form>
              </div>
            )}

            {/* Success: Points Deducted */}
            {redeemStep === "success_redeem" && (
              <div className="form-card glass-panel success-panel fade-in">
                <div className="success-icon-wrapper ring-orange">
                  <CheckCircleOutlineIcon className="success-icon text-orange" />
                </div>
                <h3 className="text-orange">Redemption Successful!</h3>
                <p className="success-message">
                  Points have been deducted from the member's account. Key the discount value into the physical POS terminal now.
                </p>

                <div className="details-box card-redeem-details">
                  <p><strong>Points Deducted:</strong> <span className="deduction-val">-{redeemResult?.points_deducted || 0} pts</span></p>
                  <p><strong>Discount Value:</strong> <span className="discount-val">RM {redeemResult?.amount_rm?.toFixed(2)}</span></p>
                  <p><strong>POS Reference:</strong> #{redeemResult?.pos_ref}</p>
                  <p><strong>Remaining Member Balance:</strong> {redeemResult?.remaining_balance || 0} pts</p>
                </div>

                <button className="btn-submit bg-orange-gradient" onClick={handleBackToLanding}>
                  Return to Home
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Styled JSX Custom CSS for Glassmorphism Theme */}
      <style jsx="true">{`
        .ops-loyalty-wrapper {
          min-height: 100vh;
          width: 100%;
          background: radial-gradient(circle at center, #111827 0%, #030712 100%);
          color: #f3f4f6;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          display: flex;
          flex-direction: column;
          overflow-x: hidden;
        }

        .ops-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 2rem;
          background: rgba(17, 24, 39, 0.7);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
          z-index: 10;
        }

        .logo-section {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .header-logo {
          height: 38px;
          width: auto;
        }

        .logo-text {
          font-size: 1.25rem;
          font-weight: 700;
          letter-spacing: -0.025em;
          color: #ffffff;
        }

        .badge-offline {
          background: rgba(59, 130, 246, 0.15);
          color: #60a5fa;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 0.2rem 0.6rem;
          border-radius: 9999px;
          border: 1px solid rgba(59, 130, 246, 0.25);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .user-section {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .user-welcome {
          font-size: 0.9rem;
          color: #9ca3af;
        }

        .user-welcome strong {
          color: #ffffff;
        }

        .btn-exit {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(239, 68, 68, 0.1);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.2);
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-exit:hover {
          background: rgba(239, 68, 68, 0.2);
          color: #ffffff;
          border-color: rgba(239, 68, 68, 0.4);
          transform: translateY(-1px);
        }

        .icon-exit {
          font-size: 1.2rem !important;
        }

        .ops-main-container {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          max-width: 1200px;
          margin: 0 auto;
          width: 100%;
        }

        .landing-view {
          text-align: center;
          width: 100%;
          max-width: 900px;
        }

        .landing-title {
          font-size: 2.75rem;
          font-weight: 800;
          letter-spacing: -0.03em;
          margin-bottom: 0.5rem;
          background: linear-gradient(135deg, #ffffff 0%, #9ca3af 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .landing-subtitle {
          color: #9ca3af;
          font-size: 1.125rem;
          margin-bottom: 3rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        .cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
          gap: 2.5rem;
          width: 100%;
        }

        .glow-card {
          position: relative;
          background: rgba(31, 41, 55, 0.4);
          border-radius: 24px;
          padding: 2.5rem;
          border: 1px solid rgba(255, 255, 255, 0.08);
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          overflow: hidden;
          text-align: left;
        }

        .glow-card:hover {
          transform: translateY(-6px);
          background: rgba(31, 41, 55, 0.6);
        }

        .earn-card:hover {
          border-color: rgba(16, 185, 129, 0.3);
          box-shadow: 0 20px 40px -15px rgba(16, 185, 129, 0.25);
        }

        .redeem-card:hover {
          border-color: rgba(245, 158, 11, 0.3);
          box-shadow: 0 20px 40px -15px rgba(245, 158, 11, 0.25);
        }

        .icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 60px;
          height: 60px;
          border-radius: 16px;
          margin-bottom: 1.5rem;
        }

        .bg-green {
          background: rgba(16, 185, 129, 0.15);
          color: #34d399;
          border: 1px solid rgba(16, 185, 129, 0.25);
        }

        .bg-orange {
          background: rgba(245, 158, 11, 0.15);
          color: #fbbf24;
          border: 1px solid rgba(245, 158, 11, 0.25);
        }

        .card-icon {
          font-size: 2rem !important;
        }

        .card-title {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 0.75rem;
          letter-spacing: -0.01em;
        }

        .text-green {
          color: #34d399;
        }

        .text-orange {
          color: #fbbf24;
        }

        .card-description {
          color: #9ca3af;
          font-size: 0.95rem;
          line-height: 1.5;
          margin-bottom: 2rem;
          min-height: 48px;
        }

        .card-action-btn {
          width: 100%;
          border: none;
          padding: 0.85rem;
          border-radius: 12px;
          font-weight: 700;
          color: #ffffff;
          cursor: pointer;
          transition: all 0.3s ease;
          letter-spacing: 0.025em;
        }

        .bg-green-gradient {
          background: linear-gradient(135deg, #059669 0%, #0d9488 100%);
          box-shadow: 0 4px 14px 0 rgba(5, 150, 105, 0.2);
        }

        .bg-green-gradient:hover {
          background: linear-gradient(135deg, #10b981 0%, #14b8a6 100%);
          box-shadow: 0 6px 20px 0 rgba(5, 150, 105, 0.4);
          transform: scale(1.02);
        }

        .bg-orange-gradient {
          background: linear-gradient(135deg, #d97706 0%, #db2777 100%);
          box-shadow: 0 4px 14px 0 rgba(217, 119, 6, 0.2);
        }

        .bg-orange-gradient:hover {
          background: linear-gradient(135deg, #f59e0b 0%, #ec4899 100%);
          box-shadow: 0 6px 20px 0 rgba(217, 119, 6, 0.4);
          transform: scale(1.02);
        }

        /* Flow Container */
        .flow-container {
          width: 100%;
          max-width: 600px;
        }

        .flow-header {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 2rem;
          width: 100%;
        }

        .btn-back {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: transparent;
          border: none;
          color: #9ca3af;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          padding: 0;
          width: fit-content;
          transition: color 0.2s ease;
        }

        .btn-back:hover {
          color: #ffffff;
        }

        .flow-title-wrapper {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .flow-title-wrapper h2 {
          font-size: 1.75rem;
          font-weight: 700;
          margin: 0;
          letter-spacing: -0.025em;
        }

        .flow-badge {
          font-size: 0.7rem;
          font-weight: 700;
          padding: 0.25rem 0.6rem;
          border-radius: 6px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        /* Form Card & Panels */
        .glass-panel {
          background: rgba(31, 41, 55, 0.45);
          backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 20px;
          padding: 2.5rem;
          box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.3);
        }

        .form-card h3 {
          font-size: 1.25rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color: #ffffff;
        }

        .step-instructions {
          color: #9ca3af;
          font-size: 0.9rem;
          margin-bottom: 2rem;
        }

        .input-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
          position: relative;
        }

        .input-group label {
          font-size: 0.95rem;
          font-weight: 600;
          color: #d1d5db;
        }

        .phone-input-container {
          display: flex;
          gap: 0.5rem;
          width: 100%;
        }

        .country-select {
          padding: 1rem;
          border-radius: 12px;
          background: rgba(17, 24, 39, 0.6);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #ffffff;
          font-size: 1.1rem;
          outline: none;
          cursor: pointer;
          transition: all 0.2s ease;
          width: 110px;
          text-align: center;
        }

        .country-select:focus {
          border-color: #3b82f6;
          background: rgba(17, 24, 39, 0.8);
        }

        .input-with-icon {
          position: relative;
          display: flex;
          align-items: center;
          width: 100%;
        }

        .input-icon {
          position: absolute;
          left: 1rem;
          color: #9ca3af;
          pointer-events: none;
        }

        .input-with-icon input {
          padding-left: 2.75rem !important;
        }

        .input-group input {
          width: 100%;
          padding: 1rem;
          border-radius: 12px;
          background: rgba(17, 24, 39, 0.6);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #ffffff;
          font-size: 1.1rem;
          transition: all 0.2s ease;
        }

        .input-group input[readonly] {
          background: rgba(31, 41, 55, 0.6) !important;
          border-color: rgba(255, 255, 255, 0.05) !important;
          color: #9ca3af !important;
          cursor: not-allowed;
        }

        .input-group input.calculated-value[readonly] {
          background: rgba(17, 24, 39, 0.8) !important;
          border-color: rgba(245, 158, 11, 0.2) !important;
          color: #fbbf24 !important;
          font-weight: 800;
          letter-spacing: 0.025em;
          box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.3);
        }

        .input-group input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
          background: rgba(17, 24, 39, 0.8);
        }

        .btn-submit {
          width: 100%;
          border: none;
          padding: 1rem;
          border-radius: 12px;
          font-weight: 700;
          font-size: 1.1rem;
          color: #ffffff;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .btn-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .help-text {
          color: #9ca3af;
          font-size: 0.8rem;
          margin-top: 0.25rem;
        }

        /* Warnings & Banners */
        .warning-banner {
          background: rgba(245, 158, 11, 0.15);
          border: 1px solid rgba(245, 158, 11, 0.25);
          color: #fbbf24;
          padding: 1rem;
          border-radius: 12px;
          display: flex;
          gap: 0.75rem;
          align-items: flex-start;
          margin-bottom: 2rem;
          font-size: 0.9rem;
          line-height: 1.4;
        }

        .warn-icon {
          flex-shrink: 0;
          color: #fbbf24;
        }

        .student-profile-summary {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
          margin-bottom: 1.5rem;
        }

        .avatar {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: rgba(16, 185, 129, 0.2);
          color: #34d399;
          font-size: 1.5rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .orange-avatar {
          background: rgba(245, 158, 11, 0.2);
          color: #fbbf24;
        }

        .profile-details h4 {
          font-size: 1.1rem;
          font-weight: 700;
          margin: 0 0 0.25rem 0;
          color: #ffffff;
        }

        .profile-details p {
          font-size: 0.85rem;
          color: #9ca3af;
          margin: 0;
        }

        .status-pill {
          font-size: 0.75rem;
          font-weight: 600;
          padding: 0.1rem 0.4rem;
          border-radius: 4px;
          text-transform: uppercase;
        }

        .status-pill.pending {
          background: rgba(245, 158, 11, 0.15);
          color: #fbbf24;
          border: 1px solid rgba(245, 158, 11, 0.2);
        }

        .status-pill.verified {
          background: rgba(16, 185, 129, 0.15);
          color: #34d399;
          border: 1px solid rgba(16, 185, 129, 0.2);
        }

        /* Success screen panels */
        .success-panel {
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .success-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 80px;
          height: 80px;
          border-radius: 50%;
          margin-bottom: 1.5rem;
        }

        .ring-green {
          background: rgba(16, 185, 129, 0.1);
          border: 4px solid rgba(16, 185, 129, 0.2);
        }

        .ring-orange {
          background: rgba(245, 158, 11, 0.1);
          border: 4px solid rgba(245, 158, 11, 0.2);
        }

        .success-icon {
          font-size: 3rem !important;
        }

        .success-message {
          color: #d1d5db;
          font-size: 1rem;
          line-height: 1.5;
          margin-bottom: 2rem;
        }

        .details-box {
          background: rgba(17, 24, 39, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 12px;
          padding: 1.5rem;
          width: 100%;
          text-align: left;
          margin-bottom: 2rem;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .details-box p {
          margin: 0;
          font-size: 0.95rem;
          color: #d1d5db;
        }

        .token-code {
          background: rgba(255, 255, 255, 0.08);
          padding: 0.2rem 0.5rem;
          border-radius: 4px;
          font-family: monospace;
          color: #fbbf24;
          font-size: 0.9rem;
        }

        .action-buttons {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          width: 100%;
        }

        .btn-action {
          width: 100%;
          border: none;
          padding: 1rem;
          border-radius: 12px;
          font-weight: 700;
          font-size: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          cursor: pointer;
          transition: all 0.3s ease;
          color: #ffffff;
        }

        .bg-whatsapp {
          background: #25d366;
          box-shadow: 0 4px 14px 0 rgba(37, 211, 102, 0.2);
        }

        .bg-whatsapp:hover {
          background: #22c35e;
          box-shadow: 0 6px 20px 0 rgba(37, 211, 102, 0.4);
          transform: translateY(-1px);
        }

        .btn-secondary {
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #ffffff;
        }

        .btn-secondary:hover {
          background: rgba(255, 255, 255, 0.15);
          transform: translateY(-1px);
        }

        .card-redeem-details {
          border-left: 4px solid #fbbf24;
        }

        .deduction-val {
          color: #f87171;
          font-weight: 700;
        }

        .discount-val {
          color: #34d399;
          font-weight: 700;
          font-size: 1.1rem;
        }

        .status-queued {
          color: #60a5fa;
          background: rgba(59, 130, 246, 0.15);
          border: 1px solid rgba(59, 130, 246, 0.2);
        }

        /* Light Theme Overrides */
        .ops-loyalty-wrapper.light {
          background: radial-gradient(circle at center, #ffffff 0%, #f3f4f6 100%);
          color: #1f2937;
        }
        .ops-loyalty-wrapper.light .ops-header {
          background: rgba(255, 255, 255, 0.85);
          border-bottom: 1px solid rgba(0, 0, 0, 0.08);
        }
        .ops-loyalty-wrapper.light .logo-text {
          color: #111827;
        }
        .ops-loyalty-wrapper.light .user-welcome {
          color: #4b5563;
        }
        .ops-loyalty-wrapper.light .user-welcome strong {
          color: #111827;
        }
        .ops-loyalty-wrapper.light .btn-theme-toggle {
          background: rgba(0, 0, 0, 0.05);
          color: #4b5563;
          border: 1px solid rgba(0, 0, 0, 0.1);
        }
        .ops-loyalty-wrapper.light .btn-theme-toggle:hover {
          background: rgba(0, 0, 0, 0.1);
          color: #111827;
        }
        .ops-loyalty-wrapper.light .landing-title {
          background: linear-gradient(135deg, #111827 0%, #4b5563 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .ops-loyalty-wrapper.light .landing-subtitle {
          color: #4b5563;
        }
        .ops-loyalty-wrapper.light .glow-card {
          background: rgba(255, 255, 255, 0.7);
          border: 1px solid rgba(0, 0, 0, 0.08);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }
        .ops-loyalty-wrapper.light .glow-card:hover {
          background: rgba(255, 255, 255, 0.95);
        }
        .ops-loyalty-wrapper.light .card-description {
          color: #4b5563;
        }
        .ops-loyalty-wrapper.light .glass-panel {
          background: rgba(255, 255, 255, 0.85);
          border: 1px solid rgba(0, 0, 0, 0.08);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
        }
        .ops-loyalty-wrapper.light .form-card h3 {
          color: #111827;
        }
        .ops-loyalty-wrapper.light .step-instructions {
          color: #4b5563;
        }
        .ops-loyalty-wrapper.light .input-group label {
          color: #374151;
        }
        .ops-loyalty-wrapper.light .country-select {
          background: rgba(255, 255, 255, 0.9);
          border: 1px solid rgba(0, 0, 0, 0.15);
          color: #111827;
        }
        .ops-loyalty-wrapper.light .input-group input {
          background: rgba(255, 255, 255, 0.9);
          border: 1px solid rgba(0, 0, 0, 0.15);
          color: #111827;
        }
        .ops-loyalty-wrapper.light .input-group input[readonly] {
          background: #e5e7eb !important;
          border-color: rgba(0, 0, 0, 0.1) !important;
          color: #4b5563 !important;
          cursor: not-allowed;
        }

        .ops-loyalty-wrapper.light .input-group input.calculated-value[readonly] {
          background: #f3f4f6 !important;
          border-color: rgba(217, 119, 6, 0.2) !important;
          color: #d97706 !important;
          font-weight: 800;
          box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
        }
        .ops-loyalty-wrapper.light .input-group input:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
        }
        .ops-loyalty-wrapper.light .details-box {
          background: rgba(0, 0, 0, 0.03);
          border: 1px solid rgba(0, 0, 0, 0.05);
        }
        .ops-loyalty-wrapper.light .details-box p {
          color: #374151;
        }
        .ops-loyalty-wrapper.light .token-code {
          background: rgba(0, 0, 0, 0.06);
          color: #b45309;
        }
        .ops-loyalty-wrapper.light .btn-secondary {
          background: rgba(0, 0, 0, 0.05);
          border: 1px solid rgba(0, 0, 0, 0.1);
          color: #1f2937;
        }
        .ops-loyalty-wrapper.light .btn-secondary:hover {
          background: rgba(0, 0, 0, 0.1);
        }
        .ops-loyalty-wrapper.light .profile-details h4 {
          color: #111827;
        }
        .ops-loyalty-wrapper.light .profile-details p {
          color: #4b5563;
        }
        .ops-loyalty-wrapper.light .btn-back {
          color: #4b5563;
        }
        .ops-loyalty-wrapper.light .btn-back:hover {
          color: #111827;
        }
        .ops-loyalty-wrapper.light .flow-title-wrapper h2 {
          color: #111827;
        }
        .ops-loyalty-wrapper.light .success-message {
          color: #374151;
        }
        .ops-loyalty-wrapper.light .avatar {
          background: rgba(16, 185, 129, 0.08);
        }
        .ops-loyalty-wrapper.light .avatar.orange-avatar {
          background: rgba(245, 158, 11, 0.08);
        }
        .ops-loyalty-wrapper.light .help-text {
          color: #6b7280;
        }
        .ops-loyalty-wrapper.light .student-profile-summary {
          border-bottom: 1px solid rgba(0, 0, 0, 0.08);
        }
        .ops-loyalty-wrapper.light .warning-banner {
          background: rgba(245, 158, 11, 0.08);
          border: 1px solid rgba(245, 158, 11, 0.15);
          color: #b45309;
        }
        .ops-loyalty-wrapper.light .warn-icon {
          color: #b45309;
        }

        .btn-theme-toggle {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(255, 255, 255, 0.08);
          color: #9ca3af;
          border: 1px solid rgba(255, 255, 255, 0.1);
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-theme-toggle:hover {
          background: rgba(255, 255, 255, 0.15);
          color: #ffffff;
          transform: translateY(-1px);
        }

        .icon-theme {
          font-size: 1.2rem !important;
        }

        /* Animations */
        .fade-in {
          animation: fadeIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 768px) {
          .ops-header {
            padding: 0.75rem 1rem;
          }
          .user-welcome {
            display: none;
          }
          .btn-theme-toggle .btn-text,
          .btn-exit .btn-text {
            display: none;
          }
          .btn-theme-toggle,
          .btn-exit {
            padding: 0.5rem;
            min-width: unset;
          }
          .ops-header .logo-text {
            display: none;
          }
          .landing-title {
            font-size: 2rem;
          }
          .cards-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          .glass-panel {
            padding: 1.5rem;
          }
          @media (max-width: 480px) {
            .country-select {
              width: 90px;
              padding: 0.75rem 0.5rem;
              font-size: 0.95rem;
            }
            .input-group input {
              padding: 0.75rem;
              font-size: 0.95rem;
            }
            .input-with-icon .input-icon {
              left: 0.75rem;
            }
            .input-with-icon input {
              padding-left: 2.25rem !important;
            }
          }
        }
      `}</style>
    </div>
  );
};

export default OpsLoyaltyPage;
